import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createcontactUs } from "../Redux/Slice/contactUs.slice";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import {
  FaEnvelope,
  FaPhone,
  FaDiscord,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaUser,
  FaUserTie,
} from "react-icons/fa";
import { FaSquareXTwitter } from "react-icons/fa6";
import BackgroundColor from "../component/BackgroundColor";
import SEOHead from "../component/SEOHead";

const initialFormState = {
  firstName: "",
  lastName: "",
  email: "",
  mobileNo: "",
  message: "",
  isAccept: false,
};

export default function Contact() {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.contactUs.loading);

  useEffect(() => {
    document.title = "Contact • YOYO";
    window.scrollTo(0, 0);
  }, []);

  const [form, setForm] = useState(initialFormState);
  const [sent, setSent] = useState(false);

  // First, fix the handleChange function to handle all form fields:
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Add a separate function for the custom checkbox toggle:
  const handleCheckboxToggle = () => {
    setForm((prev) => ({
      ...prev,
      isAccept: !prev.isAccept,
    }));
  };

  const validate = () => {
    if (
      !form.firstName ||
      !form.lastName ||
      !form.email ||
      !form.mobileNo ||
      !form.message
    ) {
      toast("Please fill all required fields.", {
        variant: "warning",
      });
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast("Please enter a valid email address.", {
        variant: "warning",
      });
      return false;
    }
    if (!/^[0-9]{10}$/.test(form.mobileNo)) {
      toast("Please enter a valid 10-digit mobile number (digits only).",
        { variant: "warning" },
      );
      return false;
    }
    if (!form.isAccept) {
      toast("You must accept the Terms and Privacy Policy.", {
        variant: "warning",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      await dispatch(createcontactUs(form));
      setForm(initialFormState);
      setSent(true);
      setTimeout(() => {
        setSent(false);
      }, 3000);
    } catch (err) {
      // Error snackbars are handled in the thunk
    }
  };

  return (
    <>
      <SEOHead
        title="Contact Us"
        description="Get in touch with the Yoyo Game support team. We're here to help with any questions about games, orders, downloads, or your account."
        keywords="contact Yoyo Game, game support, customer service, help"
        url="https://yoyokhel.com/contact"
      />
      <BackgroundColor className="pt-10">
        <div className="mx-auto w-[95%] sm:w-[92%] md:w-[90%] lg:max-w-[80%] py-12 md:py-16">
          {/* <div className="max-w-[80%] mx-auto"> */}
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10 md:mb-14"
          >
            <span className="inline-block px-3 py-1 rounded-full text-xs tracking-wider bg-white/10 text-white/90 mb-3">
              We usually reply within 24 hours
            </span>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white">
              Get in{" "}
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Touch
              </span>
            </h1>
            <p className="text-gray-400 mt-3 max-w-2xl mx-auto">
              Questions, feedback, or ideas? Drop us a line — we’re here to help
              you level up your gaming experience.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mx-auto">
            {/* Info panel */}
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="md:col-span-1 bg-[#1b1920]/80 border border-white/10 rounded-2xl p-6 backdrop-blur-md shadow-2xl flex flex-col gap-6"
            >
              <div>
                <h3 className="text-white text-xl font-bold mb-1">
                  Contact Info
                </h3>
                <p className="text-xs text-gray-400">
                  Reach out to us directly through any of these channels.
                </p>
              </div>

              <div className="flex flex-col gap-4 w-full">
                {[
                  {
                    icon: <FaEnvelope className="text-base" />,
                    label: "Email us",
                    value: (
                      <Link
                        to="https://mail.google.com/mail/?view=cm&fs=1&to=info@yoyogames.ca"
                        className="hover:text-purple-400 transition-colors cursor-none break-all"
                      >
                        info@yoyogames.ca
                      </Link>
                    ),
                  },
                  {
                    icon: <FaPhone className="text-base" />,
                    label: "Call us",
                    value: (
                      <div className="flex flex-col gap-1">
                        <a
                          href="tel:+180097976361"
                          className="hover:text-purple-400 transition-colors cursor-none"
                        >
                          1800-9797-6361
                        </a>
                        <a
                          href="tel:+180097979055"
                          className="hover:text-purple-400 transition-colors cursor-none"
                        >
                          1800-9797-9055
                        </a>
                      </div>
                    ),
                  },
                  {
                    icon: <FaMapMarkerAlt className="text-base" />,
                    label: "Location",
                    value: (
                      <div className="flex flex-col gap-2">
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href="https://www.google.com/maps/place/855+King+St+E+%23210,+Cambridge,+ON+N3H+3P2,+Canada/@43.3946841,-80.3558498,826m/data=!3m2!1e3!4b1!4m5!3m4!1s0x882b89f0377066e3:0x13fe980777df8121!8m2!3d43.3946841!4d-80.3558498?entry=ttu&g_ep=EgoyMDI2MDQyMi4wIKXMDSoASAFQAw%3D%3D"
                          className="hover:text-purple-400 transition-colors cursor-none leading-relaxed text-[13px]"
                        >
                          855 King Street East, Unit 210, Cambridge, ON, Canada
                        </a>
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href="https://www.google.com/maps/place/Shyam+Mandir+Rd,+Hukmchand+Nagar,+Althan,+Surat,+Gujarat+395007/@21.1460831,72.800298,1060m/data=!3m2!1e3!4b1!4m6!3m5!1s0x3be05203d87284f1:0xc550f071f2d34a38!8m2!3d21.1460831!4d72.800298!16s%2Fg%2F11f2v569v2?entry=ttu&g_ep=EgoyMDI2MDQwMS4wIKXMDSoASAFQAw%3D%3D"
                          className="hover:text-purple-400 transition-colors cursor-none leading-relaxed text-[13px]"
                        >
                          402–405 Astha Shoppers, Near Bhagvan Mahavir College, Vesu, Surat – 395007 (Branch)
                        </a>
                      </div>
                    ),
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-3.5 rounded-xl bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04] hover:border-purple-500/20 transition-all duration-300 group"
                  >
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-500/10 text-purple-400 shrink-0 group-hover:bg-purple-500 group-hover:text-white transition-all duration-300">
                      {item.icon}
                    </div>
                    <div className="flex flex-col gap-0.5 text-left min-w-0">
                      <span className="text-[11px] font-semibold text-purple-400/80 uppercase tracking-wider">
                        {item.label}
                      </span>
                      <div className="text-[13px] text-gray-300 leading-normal font-medium">
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* <div className="h-px bg-white/10 my-1" /> */}
              {/* <div>
              <p className="text-[11px] font-semibold text-purple-400/80 uppercase tracking-wider mb-3">
                Join our community
              </p>
              <div className="flex gap-3">
                {[
                  { Icon: FaDiscord, url: "https://discord.gg", label: "Join us on Discord" },
                  { Icon: FaSquareXTwitter, url: "https://twitter.com", label: "Follow us on Twitter" },
                ].map(({ Icon, url, label }, idx) => (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    key={idx}
                    aria-label={label}
                    className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/[0.02] border border-white/[0.04] text-gray-400 hover:bg-purple-500 hover:border-purple-400 hover:text-white hover:-translate-y-0.5 hover:shadow-[0_0_12px_rgba(168,85,247,0.4)] transition-all duration-300 cursor-none"
                  >
                    <Icon size={17} />
                  </a>
                ))}
              </div>
            </div> */}
            </motion.div>

            {/* Form panel */}
            <motion.div
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="md:col-span-2 relative"
            >
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-2xl blur opacity-50" />
              <div className="relative bg-[#1b1920]/90 border border-white/10 rounded-2xl p-6 md:p-8 shadow-2xl">
                {sent && (
                  <div className="mb-5 flex items-center gap-2 text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 rounded-xl px-4 py-3 text-sm">
                    <FaCheckCircle className="text-emerald-400 shrink-0 text-base" />
                    <span className="font-medium">Thanks! Your message has been sent.</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        First Name<span className="text-purple-500 ml-0.5">*</span>
                      </label>
                      <div className="relative group/input flex items-center">
                        <input
                          type="text"
                          name="firstName"
                          value={form.firstName}
                          onChange={handleChange}
                          className="w-full cursor-none bg-white/[0.02] border border-white/[0.08] hover:border-white/[0.15] focus:border-purple-500 focus:bg-white/[0.04] rounded-xl px-4 py-3 pl-11 focus:outline-none transition-all duration-300 text-white placeholder-gray-500 text-sm"
                          placeholder="John"
                        />
                        <span className="absolute left-4 text-gray-500 group-focus-within/input:text-purple-400 transition-colors duration-300 pointer-events-none">
                          <FaUser size={15} />
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Last Name<span className="text-purple-500 ml-0.5">*</span>
                      </label>
                      <div className="relative group/input flex items-center">
                        <input
                          type="text"
                          name="lastName"
                          value={form.lastName}
                          onChange={handleChange}
                          className="w-full cursor-none bg-white/[0.02] border border-white/[0.08] hover:border-white/[0.15] focus:border-purple-500 focus:bg-white/[0.04] rounded-xl px-4 py-3 pl-11 focus:outline-none transition-all duration-300 text-white placeholder-gray-500 text-sm"
                          placeholder="Doe"
                        />
                        <span className="absolute left-4 text-gray-500 group-focus-within/input:text-purple-400 transition-colors duration-300 pointer-events-none">
                          <FaUserTie size={15} />
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Email<span className="text-purple-500 ml-0.5">*</span>
                      </label>
                      <div className="relative group/input flex items-center">
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          className="w-full cursor-none bg-white/[0.02] border border-white/[0.08] hover:border-white/[0.15] focus:border-purple-500 focus:bg-white/[0.04] rounded-xl px-4 py-3 pl-11 focus:outline-none transition-all duration-300 text-white placeholder-gray-500 text-sm"
                          placeholder="you@example.com"
                        />
                        <span className="absolute left-4 text-gray-500 group-focus-within/input:text-purple-400 transition-colors duration-300 pointer-events-none">
                          <FaEnvelope size={15} />
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Mobile No<span className="text-purple-500 ml-0.5">*</span>
                      </label>
                      <div className="relative group/input flex items-center">
                        <input
                          type="tel"
                          name="mobileNo"
                          value={form.mobileNo}
                          onChange={handleChange}
                          className="w-full cursor-none bg-white/[0.02] border border-white/[0.08] hover:border-white/[0.15] focus:border-purple-500 focus:bg-white/[0.04] rounded-xl px-4 py-3 pl-11 focus:outline-none transition-all duration-300 text-white placeholder-gray-500 text-sm"
                          placeholder="15551234567"
                        />
                        <span className="absolute left-4 text-gray-500 group-focus-within/input:text-purple-400 transition-colors duration-300 pointer-events-none">
                          <FaPhone size={15} />
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Message<span className="text-purple-500 ml-0.5">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      className="w-full cursor-none bg-white/[0.02] border border-white/[0.08] hover:border-white/[0.15] focus:border-purple-500 focus:bg-white/[0.04] rounded-xl px-4 py-3 focus:outline-none transition-all duration-300 text-white placeholder-gray-500 text-sm min-h-[140px]"
                      placeholder="Tell us how we can help..."
                    />
                  </div>

                  <label className="inline-flex items-center gap-3 text-xs md:text-sm text-gray-400 select-none cursor-none group/check">
                    <span
                      role="checkbox"
                      aria-checked={form.isAccept}
                      onClick={handleCheckboxToggle}
                      className={`h-5 w-5 rounded-md border flex items-center justify-center transition-all duration-200 cursor-none ${form.isAccept
                          ? "bg-purple-600 border-purple-500 text-white shadow-[0_0_8px_rgba(168,85,247,0.4)]"
                          : "border-white/20 bg-white/[0.02] group-hover/check:border-white/40"
                        }`}
                    >
                      {form.isAccept && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </span>
                    <span>
                      I agree with{" "}
                      <Link
                        to="/privacyPolicy"
                        className="text-purple-400 hover:text-purple-300 cursor-none transition-colors"
                      >
                        Privacy Policy
                      </Link>{" "}
                      and{" "}
                      <Link
                        to="/termsCondition"
                        className="text-purple-400 hover:text-purple-300 cursor-none transition-colors"
                      >
                        Terms & Conditions
                      </Link>
                    </span>
                  </label>

                  <div className="pt-2 text-center lg:text-left">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-purple-800 disabled:to-pink-800 disabled:opacity-60 text-white font-bold rounded-xl shadow-[0_0_15px_rgba(168,85,247,0.25)] hover:shadow-[0_0_20px_rgba(168,85,247,0.45)] hover:-translate-y-0.5 transition-all duration-300 cursor-none"
                    >
                      {loading && (
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                      )}
                      {loading ? "Sending..." : "Send Message"}
                    </motion.button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
          {/* </div> */}
        </div>
      </BackgroundColor>
    </>
  );
}
